import React from 'react';
import {
  getSlideScreenShareMedia,
  getSlideWebcamUserMedia,
  getSlideWebcamUserMediaSmartDefault,
} from '../../utils/programSlideWebcamQuality';

export type ScanImageLiveCaptureMode = 'camera' | 'screen';

export type ScanImageBlockLiveCaptureProps = {
  active: boolean;
  captureMode: ScanImageLiveCaptureMode;
  cameraDeviceId: string | null;
  /** 화면 공유 종료·취소 시 카메라로 되돌릴 때 */
  onCaptureModeChange: (mode: ScanImageLiveCaptureMode) => void;
  onCapture: (blob: Blob) => void;
  onError: (message: string) => void;
};

/** 스캔 이미지 블럭 안에서 웹캠·화면 미리보기 → 클릭 시 촬영(입력 선택은 툴바) */
const ScanImageBlockLiveCapture: React.FC<ScanImageBlockLiveCaptureProps> = ({
  active,
  captureMode,
  cameraDeviceId,
  onCaptureModeChange,
  onCapture,
  onError,
}) => {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const capturePendingRef = React.useRef(false);
  const captureGenRef = React.useRef(0);
  const screenEndedCleanupRef = React.useRef<(() => void) | null>(null);
  const onCaptureModeChangeRef = React.useRef(onCaptureModeChange);
  const onErrorRef = React.useRef(onError);
  onCaptureModeChangeRef.current = onCaptureModeChange;
  onErrorRef.current = onError;

  const stopStream = React.useCallback(() => {
    screenEndedCleanupRef.current?.();
    screenEndedCleanupRef.current = null;
    const s = streamRef.current;
    if (s) {
      s.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    const v = videoRef.current;
    if (v) v.srcObject = null;
  }, []);

  React.useEffect(() => {
    if (!active) {
      stopStream();
      capturePendingRef.current = false;
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      onErrorRef.current('이 환경에서는 카메라를 사용할 수 없습니다. HTTPS 접속인지 확인해 주세요.');
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        stopStream();
        if (cancelled) return;

        if (captureMode === 'screen') {
          let stream: MediaStream;
          try {
            stream = await getSlideScreenShareMedia();
          } catch (err: unknown) {
            if (cancelled) return;
            const name = err && typeof err === 'object' && 'name' in err ? String((err as { name?: string }).name) : '';
            if (name === 'NotAllowedError') {
              onCaptureModeChangeRef.current('camera');
              return;
            }
            const msg = err instanceof Error ? err.message : '화면 공유를 시작할 수 없습니다.';
            onErrorRef.current(msg);
            return;
          }

          if (cancelled) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }

          streamRef.current = stream;
          const t0 = stream.getVideoTracks()[0];
          if (t0) {
            const onEnd = () => {
              if (cancelled) return;
              onCaptureModeChangeRef.current('camera');
            };
            t0.addEventListener('ended', onEnd);
            screenEndedCleanupRef.current = () => {
              t0.removeEventListener('ended', onEnd);
            };
          }

          const attachScreen = () => {
            if (cancelled) {
              stream.getTracks().forEach((tr) => tr.stop());
              return;
            }
            const v = videoRef.current;
            if (!v) {
              requestAnimationFrame(attachScreen);
              return;
            }
            v.srcObject = stream;
            void v.play().catch(() => {});
          };
          attachScreen();
          return;
        }

        const stream = cameraDeviceId
          ? await getSlideWebcamUserMedia({ deviceId: cameraDeviceId })
          : await getSlideWebcamUserMediaSmartDefault();
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const attachCam = () => {
          if (cancelled) {
            stream.getTracks().forEach((tr) => tr.stop());
            return;
          }
          const v = videoRef.current;
          if (!v) {
            requestAnimationFrame(attachCam);
            return;
          }
          v.srcObject = stream;
          void v.play().catch(() => {});
        };
        attachCam();
      } catch (err: unknown) {
        if (cancelled) return;
        const name = err && typeof err === 'object' && 'name' in err ? String((err as { name?: string }).name) : '';
        const msg =
          name === 'NotAllowedError'
            ? '카메라 사용이 거부되었습니다.'
            : err instanceof Error
              ? err.message
              : '카메라를 켤 수 없습니다.';
        onErrorRef.current(msg);
      }
    })();

    return () => {
      cancelled = true;
      stopStream();
    };
  }, [active, captureMode, cameraDeviceId, stopStream]);

  const handleCaptureClick = React.useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (capturePendingRef.current) return;

      const v = videoRef.current;
      if (!v || !streamRef.current) return;

      const w = v.videoWidth;
      const h = v.videoHeight;
      if (w <= 0 || h <= 0) {
        onErrorRef.current('영상을 불러오는 중입니다. 잠시 후 다시 시도해 주세요.');
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      try {
        ctx.drawImage(v, 0, 0, w, h);
      } catch {
        onErrorRef.current('촬영에 실패했습니다.');
        return;
      }

      capturePendingRef.current = true;
      captureGenRef.current += 1;
      const gen = captureGenRef.current;

      canvas.toBlob(
        (blob) => {
          capturePendingRef.current = false;
          if (!blob || gen !== captureGenRef.current) return;
          stopStream();
          onCapture(blob);
        },
        'image/jpeg',
        0.94
      );
    },
    [onCapture, stopStream]
  );

  const stopPointerFromCanvas = React.useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
  }, []);

  if (!active) return null;

  return (
    <div className="cp-program-slide-scan-live" role="presentation">
      <div
        role="button"
        tabIndex={0}
        className="cp-program-slide-scan-live-hit"
        aria-label="미리보기. 클릭하여 촬영합니다."
        onPointerDown={stopPointerFromCanvas}
        onPointerUp={handleCaptureClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCaptureClick(e);
          }
        }}
      >
        <video
          ref={videoRef}
          className="cp-program-slide-scan-live-video"
          playsInline
          muted
          autoPlay
          aria-hidden
        />
        <span className="cp-program-slide-scan-live-hint">클릭하여 촬영</span>
      </div>
    </div>
  );
};

export default ScanImageBlockLiveCapture;
